import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LogoutButton from "./LogoutButton";
import logo from "../assets/images/White_Version.png";
import { useLocation } from "react-router-dom";
import { Star } from "lucide-react";

import { getAuthCookie } from "../utils/authCookies";

interface ModulePermission {
  id: string;
  name: string;
  module_code: string;
  license_type_code: string;
  status: boolean;
  volume: number;
}

interface Module {
  id: string;
  name: string;
  module_code: string;
  license_type_code: string;
  description: string;
  status: boolean;
  volume: number;
}

interface License {
  id: string;
  companyName: string;
  jobTitle: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  zip: string;
  statuss: string;
  licenseValidity: string | null;
  billingCycle: string | null;
  billingCyclePrice: number | null;
  paymentMethod: string;
  licenseType: string;
  modules: Module[]; // Changed to Module[]
}

const sidebarItems = [
  {
    name: "Brands",
    path: "/agent/brands",
    icon: "ri-store-2-line",
    module_code: "BRANDS",
  },
  {
    name: "Categories",
    path: "/agent/categories",
    icon: "ri-store-2-line",
    module_code: "CATEGORIES",
  },
  {
    name: "Inventory",
    path: "/agent/inventory",
    icon: "ri-building-2-line",
    module_code: "INVENTORY",
  },
  {
    name: "Vendors",
    path: "/agent/suppliers",
    icon: "ri-group-line",
    module_code: "SUPPLIERS",
  },
  {
    name: "Orders",
    path: "/agent/orders",
    icon: "bx bx-cart-alt",
    module_code: "ORDERS",
  },
  {
    name: "Design Requests",
    path: "/agent/design-requests",
    icon: "ri-folder-chart-line",
    module_code: "PROJECTS",
  },
  {
    name: "Jobs",
    path: "/agent/jobs",
    icon: "ri-group-line",
    module_code: "JOBS",
  },
  {
    name: "Batch Payments",
    path: "/agent/batchPayments",
    icon: "ri-file-text-line",
    module_code: "BATCH_PAYMENTS",
  },
  {
    name: "Reports",
    path: "/agent/reports",
    icon: "ri-bar-chart-2-fill",
    module_code: "REPORTS",
  },
  {
    name: "Addresses",
    path: "/agent/addresses",
    icon: "ri-map-pin-2-line",
    module_code: "ADDRESSES",
  },
];

const AgentSidebar = () => {
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const [license, setLicense] = useState<License | null>(null);
  const [modulePermissions, setModulePermissions] = useState<Module[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  // useEffect(() => {
  //   const path = location.pathname;

  //   if (path.startsWith("/signage/jobs")) {
  //     setOpenMenu("jobsMenu");
  //   }
  //   else if (path.startsWith("/signage/products")) {
  //     setOpenMenu("inventoryMenu");
  //   }
  //   else if (path.startsWith("/signage/orders")) {
  //     setOpenMenu("salesMenu");
  //   }
  //   else if (path.startsWith("/signage/roles")) {
  //     setOpenMenu("teamMenu");
  //   }
  // }, [location.pathname]);

  useEffect(() => {
    const fetchLicense = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${userId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch license data");
        }

        const data = await response.json();
        // console.log("Fetched License Data:", data);

        if (data && data.firmDetails && data.firmDetails.address) {
          const fetchedLicense: License = {
            id: data.id || "",
            companyName: data.firmDetails.companyName || "",
            jobTitle: data.jobTitle || "",
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            email: data.email || "",
            phone: data.phone1 || "",
            addressLine1: data.firmDetails.address.addressLine1 || "",
            addressLine2: data.firmDetails.address.addressLine2 || "",
            city: data.firmDetails.address.city || "",
            state: data.firmDetails.address.state || "",
            zip: data.firmDetails.address.zip || "",
            country: data.firmDetails.address.country || "",
            statuss: data.status || "Active",
            licenseValidity: data.licenseValidity || null,
            billingCycle: data.billingCycle || null,
            billingCyclePrice: data.billingCyclePrice || null,
            paymentMethod: data.paymentMethod || "",
            licenseType: data.licenseType || "",
            modules: Array.isArray(data.modulePermissions)
              ? data.modulePermissions
              : [],
          };

          setLicense(fetchedLicense);
          setModulePermissions(
            Array.isArray(data.modulePermissions) ? data.modulePermissions : []
          );
        } else {
          console.error("Incomplete data", data);
        }
      } catch (error) {
        console.error("Error fetching license data:", error);
        setError("Failed to fetch license data.");
      } finally {
        setLoading(false);
      }
    };

    if (!license) {
      fetchLicense();
    }
  }, [userId, license, token]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (window.innerWidth < 992) {
      document.body.classList.remove("vertical-sidebar-enable", "sidebar-enable");
    }
  }, [location.pathname]);

  return (
    <>
      {/* 🔐 Scoped, in-file CSS for Agent sidebar */}
      <style>{`
        /* Scope everything to #agent-sidebar to prevent leaks */
        #agent-sidebar .navbar-menu { overflow-y: auto; scrollbar-width: none; }

        /* MOBILE (≤767px): off-canvas + overlay */
        @media (max-width: 767px) {
          #agent-sidebar .app-menu.navbar-menu {
            position: fixed;
            top: 0; left: 0; bottom: 0;
            width: 280px;
            z-index: 1040;              /* above overlay */
            transform: translateX(-100%);
            transition: transform .3s ease;
            background: #0b1324;        /* ensure solid bg */
          }
          body.vertical-sidebar-enable #agent-sidebar .app-menu.navbar-menu {
            transform: translateX(0);
          }

          #agent-sidebar .mobile-sidebar-header {
            position: sticky;
            top: 0;
            z-index: 3;
            background: #0b1324;
            border-bottom: 1px solid rgba(255,255,255,.08);
          }

          /* Hide desktop brand on mobile */
          #agent-sidebar .navbar-brand-box { display: none !important; }

          /* Light overlay */
          #agent-sidebar .sidebar-background {
            position: fixed;
            inset: 0;
            background: rgba(11,19,36,.3);
            opacity: 0;
            pointer-events: none;
            transition: opacity .2s ease;
            z-index: 1030; /* below sidebar */
          }
          body.vertical-sidebar-enable #agent-sidebar .sidebar-background {
            opacity: .5;
            pointer-events: auto;
          }

          /* Lock page scroll while sidebar open */
          body.vertical-sidebar-enable { overflow: hidden; }
        }

        /* TABLET & DESKTOP (≥768px): static sidebar; no overlay */
        @media (min-width: 768px) {
          #agent-sidebar .app-menu.navbar-menu { position: static; transform: none !important; z-index: auto; }
          #agent-sidebar .sidebar-background { display: none; }
        }
      `}</style>
      <div className="app-menu navbar-menu">
        {/* ✅ Mobile top strip: logo + close (visible only on ≤767px) */}
        <div className="mobile-sidebar-header d-md-none d-flex align-items-center gap-2 px-3 py-2">
          <Link to="/agent/welcome" className="d-inline-flex align-items-center text-decoration-none">
            <img src={logo} alt="Agent" height={100} />
          </Link>
          <button
            type="button"
            aria-label="Close sidebar"
            className="btn btn-sm btn-icon btn-ghost-light ms-auto mt-3"
            onClick={() => {
              document.body.classList.remove("vertical-sidebar-enable", "sidebar-enable");
            }}
          >
            <i className="ri-close-line fs-4" />
          </button>
        </div>
        <div className="navbar-brand-box d-none d-md-block">
          {/* Logo */}
          <Link to="/agent/welcome" className="logo logo-dark">
            <span className="logo-sm">
              <img src={logo} alt="" height="22" />
            </span>
            <span className="logo-lg">
              <img src={logo} alt="" height="40" />
            </span>
          </Link>

          <Link to="/agent/welcome" className="logo logo-light">
            <span className="logo-sm">
              <img src={logo} alt="" height="22" />
            </span>
            <span className="logo-lg">
              <img src={logo} alt="" height="90" style={{ marginTop: "10px" }} />
            </span>
          </Link>

          <button
            type="button"
            className="btn btn-sm p-0 fs-20 header-item float-end btn-vertical-sm-hover"
            id="vertical-hover"
          >
            <i className="ri-record-circle-line"></i>
          </button>
        </div>

        {/* User Dropdown */}
        <div className="dropdown sidebar-user m-1 rounded">
          <button
            type="button"
            className="btn material-shadow-none"
            id="page-header-user-dropdown"
            data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            <span className="d-flex align-items-center gap-2">
              <img
                className="rounded header-profile-user"
                src="/assets/images/users/avatar-1.jpg"
                alt="Header Avatar"
              />
              <span className="text-start">
                <span className="d-block fw-medium sidebar-user-name-text">
                  Anna Adame
                </span>
                <span className="d-block fs-14 sidebar-user-name-sub-text">
                  <i className="ri-circle-fill fs-10 text-success align-baseline"></i>{" "}
                  <span className="align-middle">Online</span>
                </span>
              </span>
            </span>
          </button>
          <div className="dropdown-menu dropdown-menu-end">
            <h6 className="dropdown-header">Welcome Anna!</h6>
            <Link to="/profile" className="dropdown-item">
              <i className="mdi mdi-account-circle text-muted fs-16 align-middle me-1"></i>{" "}
              <span className="align-middle">Profile</span>
            </Link>
            <Link to="/messages" className="dropdown-item">
              <i className="mdi mdi-message-text-outline text-muted fs-16 align-middle me-1"></i>{" "}
              <span className="align-middle">Messages</span>
            </Link>
            <Link to="/tasks" className="dropdown-item">
              <i className="mdi mdi-calendar-check-outline text-muted fs-16 align-middle me-1"></i>{" "}
              <span className="align-middle">Taskboard</span>
            </Link>
            <Link to="/help" className="dropdown-item">
              <i className="mdi mdi-lifebuoy text-muted fs-16 align-middle me-1"></i>{" "}
              <span className="align-middle">Help</span>
            </Link>
            <div className="dropdown-divider"></div>
            <Link to="/profile" className="dropdown-item">
              <i className="mdi mdi-wallet text-muted fs-16 align-middle me-1"></i>{" "}
              <span className="align-middle">
                Balance: <b>$5971.67</b>
              </span>
            </Link>
            <Link to="/settings" className="dropdown-item">
              <i className="mdi mdi-cog-outline text-muted fs-16 align-middle me-1"></i>{" "}
              <span className="align-middle">Settings</span>
            </Link>
            <Link to="/lockscreen" className="dropdown-item">
              <i className="mdi mdi-lock text-muted fs-16 align-middle me-1"></i>{" "}
              <span className="align-middle">Lock screen</span>
            </Link>
            <Link to="/logout" className="dropdown-item">
              <i className="mdi mdi-logout text-muted fs-16 align-middle me-1"></i>{" "}
              <span className="align-middle">Logout</span>
            </Link>
          </div>
        </div>

        {/* Sidebar Navigation */}
        <div id="scrollbar">
          <div className="container-fluid">
            <ul className="navbar-nav" id="navbar-nav">
              {/* <li className="nav-item">
                <Link to="/agent/welcome" className={`nav-link menu-link ${location.pathname === "/agent/welcome" ? "active" : ""}`}>
                    <Star size={28} color="#ffffff" className="ms-1" />
                  <span data-key="t-dashboards">Welcome</span>
                </Link>
              </li> */}
              <li className="nav-item">
                <Link to="/agent/dashboard" className={`nav-link menu-link ${location.pathname === "/agent/dashboard" ? "active" : ""}`}>
                  <i className="ri-dashboard-fill"></i>{" "}
                  <span data-key="t-dashboards">Dashboard</span>
                </Link>
              </li>
              {sidebarItems?.map((item) => {
                const permission = modulePermissions?.find((perm) => perm.module_code === item.module_code);
                if (item.module_code === "JOBS") {
                  return (
                    <li className="nav-item" key={item.module_code}>
                      <a
                        className="nav-link menu-link"
                        onClick={() => setOpenMenu(openMenu === 'jobsMenu' ? null : 'jobsMenu')}
                        href="#jobs-menu"
                        data-bs-toggle="collapse"
                      >
                        <i className={item.icon}></i> <span>{item.name}</span>
                      </a>
                      <div className={`collapse menu-dropdown ${openMenu === 'jobsMenu' ? 'show' : ''}`} id="jobs-menu">
                        <ul className="nav nav-sm flex-column">
                          <li className="nav-item">
                            <Link to="/agent/jobs/installations" className={`nav-link menu-link ${location.pathname === "/agent/jobs/installations" ? "active" : ""}`}>Installations</Link>
                          </li>
                          <li className="nav-item">
                            <Link to="/agent/jobs/removalRequests" className={`nav-link menu-link ${location.pathname === "/agent/jobs/removalRequests" ? "active" : ""}`}>Removal Requests</Link>
                          </li>
                          <li className="nav-item">
                            <Link to="/agent/active-installations" className={`nav-link menu-link ${location.pathname === "/agent/active-installations" ? "active" : ""}`}>Active Installations</Link>
                          </li>
                        </ul>
                      </div>
                    </li>
                  );
                }

                if (item.module_code === "REPORTS") {
                  return (
                    <li className="nav-item" key={item.module_code}>
                      <a
                        className="nav-link menu-link"
                        onClick={() => setOpenMenu(openMenu === 'reportsMenu' ? null : 'reportsMenu')}
                        href="#reports-menu"
                        data-bs-toggle="collapse"
                      >
                        <i className={item.icon}></i> <span>{item.name}</span>
                      </a>
                      <div className={`collapse menu-dropdown ${openMenu === 'reportsMenu' ? 'show' : ''}`} id="reports-menu">
                        <ul className="nav nav-sm flex-column">
                          <li className="nav-item">
                            <Link to="/agent/installationSummaryReport" className={`nav-link menu-link ${location.pathname === "/agent/installationSummaryReport" ? "active" : ""}`}>Installation Summary Report</Link>
                          </li>
                          <li className="nav-item">
                            <Link to="/agent/removalSummaryReport" className={`nav-link menu-link ${location.pathname === "/agent/removalSummaryReport" ? "active" : ""}`}>Removal Summary Report</Link>
                          </li>
                          <li className="nav-item">
                            <Link to="/agent/salesOrderReport" className={`nav-link menu-link ${location.pathname === "/agent/salesOrderReport" ? "active" : ""}`}>SalesOrder Summary Report</Link>
                          </li>
                          {/* <li className="nav-item">
                          <Link to="/agent/active-installations" className={`nav-link menu-link ${location.pathname === "/agent/active-installations" ? "active" : ""}`}>Active Installations</Link>
                        </li> */}
                        </ul>
                      </div>
                    </li>
                  );
                }

                return permission?.status ? (
                  <li className="nav-item" key={item.module_code}>
                    <Link to={item.path}
                      className={`nav-link menu-link ${location.pathname === item.path ? "active" : ""}`}
                    >
                      <i className={item.icon}></i> <span>{item.name}</span>
                    </Link>
                  </li>
                ) : null;
              })}

              <li className="nav-item">
                <Link to="/agent/profile"
                  className={`nav-link menu-link ${location.pathname === "/agent/profile" ? "active" : ""}`}
                >
                  <i className="ri-user-line"></i>{" "}
                  <span data-key="t-dashboards">Profile</span>
                </Link>
              </li>
              <li className="nav-item">
                <LogoutButton />
              </li>
            </ul>
          </div>
        </div>

        <div className="sidebar-background"></div>
      </div>
      {/* ✅ Overlay outside the sidebar (click to close) */}
      <div
        className="sidebar-background"
        onClick={() => {
          document.body.classList.remove("vertical-sidebar-enable", "sidebar-enable");
        }}
      />
    </>
  );
};

export default AgentSidebar;
